/**
 * v2.42 — "Mind, Music & Motivation" rail (circular tile variant).
 *
 * Instagram-Stories style circular avatars (mirrors HomeLiveTV.jsx), so
 * Channel rails on Home are visually consistent. Each tile:
 *   - Themed gradient ring (per-pill color)
 *   - Channel initials in the center (since we don't have YT avatars
 *     without an API key)
 *   - Channel name + pill label below
 *   - Click → opens YouTube channel in a new tab
 *
 * Sub-categories rendered as inline pills (Family / Documentaries / Horror
 * pattern), auto-shuffle every 25 seconds.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Headphones, Sparkles, Shuffle } from "lucide-react";
import { api } from "../lib/api";

// Fallback static pills list (shown while API loads or on error)
const MMM_PILLS = ["focus", "energy", "chill", "bhakti", "podcasts"];

// Fisher-Yates shuffle.
function shuffleArr(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Compute up-to-2-letter initials from a channel name. Used as the circle's
// "avatar" fallback when the YouTube channel-thumbnail image fails to load
// (rare — unavatar.io handles 95%+ of YT @-handles).
function initials(name) {
  const words = name
    .replace(/[—–|·]/g, " ")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// v2.49 — ChannelAvatar: renders the YouTube channel's REAL avatar via
// unavatar.io (free service that resolves social handles to avatars, returns
// a server-side image so no client-side YT API call needed). Falls back to
// initials if the request fails or the URL is blocked.
function ChannelAvatar({ handle, name, fillClass }) {
  // unavatar.io expects the handle WITHOUT the leading @
  const handleClean = (handle || "").replace(/^@/, "");
  const src = `https://unavatar.io/youtube/${handleClean}?fallback=false`;
  const [errored, setErrored] = useState(false);
  if (errored || !handleClean) {
    return (
      <div className={`w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] rounded-full bg-gradient-to-br ${fillClass} flex items-center justify-center border border-white/5`}>
        <span className="font-display font-bold text-xl text-white/95 tracking-wider drop-shadow">
          {initials(name)}
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
      className="w-[72px] h-[72px] sm:w-[84px] sm:h-[84px] rounded-full object-cover border border-white/5 bg-zinc-900"
    />
  );
}

// Per-pill ring colors. Match the dark-mode palette of HomeLiveTV's red ring
// while differentiating each Mind/Music/Motivation sub-category.
const PILL_RING = {
  focus:    "from-indigo-400 via-violet-500 to-fuchsia-500",
  energy:   "from-rose-500 via-amber-500 to-orange-500",
  chill:    "from-teal-400 via-sky-400 to-cyan-400",
  bhakti:   "from-amber-400 via-yellow-500 to-rose-400",
  podcasts: "from-sky-400 via-blue-500 to-indigo-500",
};
const PILL_FILL = {
  focus:    "from-indigo-900 to-violet-950",
  energy:   "from-rose-900 to-orange-950",
  chill:    "from-teal-900 to-sky-950",
  bhakti:   "from-amber-900 to-rose-950",
  podcasts: "from-sky-900 to-indigo-950",
};
const PILL_LABEL = {
  focus: "Focus", energy: "Energy", chill: "Chill",
  bhakti: "Bhakti", podcasts: "Podcast",
};

export default function HomeMindMusicMotivation() {
  const [pill, setPill] = useState("all");
  const [shuffleKey, setShuffleKey] = useState(0);
  const [allChannels, setAllChannels] = useState([]);
  const scrollRef = useRef(null);

  // Fetch from backend API (falls back to empty → no render until loaded)
  const fetchChannels = useCallback(async () => {
    try {
      const res = await api.get("/mmm/channels");
      setAllChannels(res.data.channels || []);
    } catch {
      // keep empty — section is hidden if no data
    }
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  // Auto-shuffle every 25s
  useEffect(() => {
    const t = setInterval(() => setShuffleKey((k) => k + 1), 25000);
    return () => clearInterval(t);
  }, []);

  const channels = useMemo(() => {
    const filtered = pill === "all" ? allChannels : allChannels.filter(c => c.pill === pill);
    return shuffleArr(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pill, shuffleKey, allChannels]);

  if (allChannels.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: dir * w * 0.7, behavior: "smooth" });
  };

  return (
    <section
      data-testid="row-mind-music-motivation-wrap"
      className="relative px-6 lg:px-10 py-7 bg-gradient-to-br from-indigo-950/30 via-zinc-950/40 to-violet-950/30 border-y border-indigo-900/20 fm-fade max-w-[1440px] mx-auto"
    >
      <div className="mb-4">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/30 to-fuchsia-500/30 border border-white/15 flex items-center justify-center">
              <Headphones className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight leading-none">
                Mind, Music &amp; Motivation
                <Sparkles className="inline-block ml-2 w-4 h-4 text-amber-300/90" />
              </h2>
              <p className="text-[11px] text-neutral-400 mt-1">
                Curated YouTube channels for the productive community
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShuffleKey((k) => k + 1)}
              data-testid="mmm-shuffle"
              aria-label="Shuffle channels"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-300 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition"
            >
              <Shuffle className="w-3.5 h-3.5" /> Shuffle
            </button>
            <button
              onClick={() => scroll(-1)}
              data-testid="mmm-scroll-left"
              aria-label="Scroll left"
              className="p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/80 hidden sm:block"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll(1)}
              data-testid="mmm-scroll-right"
              aria-label="Scroll right"
              className="p-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/80 hidden sm:block"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="mmm-pills">
          {MMM_PILLS.map((p) => (
            <button
              key={p.slug}
              onClick={() => setPill(p.slug)}
              data-testid={`mmm-pill-${p.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition border ${
                pill === p.slug
                  ? "bg-indigo-500/30 text-indigo-100 border-indigo-400/60"
                  : "bg-white/[0.03] text-white/65 border-white/10 hover:bg-white/[0.08] hover:text-white/90"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-white/10 snap-x snap-mandatory"
        data-testid="mmm-scroll"
      >
        {channels.map((ch, idx) => {
          const ringClass = PILL_RING[ch.pill] || PILL_RING.focus;
          const fillClass = PILL_FILL[ch.pill] || PILL_FILL.focus;
          return (
            <a
              key={`${ch.handle}-${idx}`}
              href={`https://www.youtube.com/${ch.handle}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`mmm-card-${idx}`}
              className="group shrink-0 w-[88px] sm:w-[100px] snap-start flex flex-col items-center"
              title={`${ch.name} — ${ch.blurb}`}
            >
              {/* Avatar — gradient ring around the YouTube channel thumbnail
                  (resolved via unavatar.io which maps YT @-handles to their
                  real channel avatars). Falls back to initials if the image
                  fails to load (e.g. channel renamed, network glitch). */}
              <div className="relative">
                <div className={`p-[2.5px] rounded-full bg-gradient-to-br ${ringClass} group-hover:scale-105 transition-transform`}>
                  <div className="p-[2px] rounded-full bg-black">
                    <ChannelAvatar
                      handle={ch.handle}
                      name={ch.name}
                      fillClass={fillClass}
                    />
                  </div>
                </div>
                {/* Headphones badge (instead of LIVE dot) */}
                <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-full bg-indigo-500 text-[9px] font-bold text-white flex items-center gap-0.5 shadow-lg">
                  <Headphones className="w-2.5 h-2.5" />
                  {PILL_LABEL[ch.pill] || "YT"}
                </div>
              </div>
              <div className="mt-2 text-center w-full">
                <p className="text-[11px] font-semibold text-white truncate" title={ch.name}>
                  {ch.name}
                </p>
                <p className="text-[9px] text-white/40 uppercase tracking-wide truncate">
                  {ch.handle}
                </p>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
